import React, { useEffect, useMemo, useState } from 'react';
import { createCn } from '../../../helpers/utils';
import { KeywordStat, KeywordTheme, SpeechSegment } from '../../../services/api.service';
import { ThemeIcon } from '../../../components/icons/theme-icon.component';
import { formatSpeakerFullName, getSegmentStartSec } from '../utils/audio-segments';
import {
  buildKeywordReplicas,
  countKeywordReplicas,
  formatKeywordLabel,
  highlightWords,
  normalizeThemes,
  resolveSpeakerColor,
} from '../utils/meeting-analytics';
import classes from '../styles.module.scss';

const cn = createCn('file', classes);

type SpeakerTimeline = {
  name: string;
  color: string;
};

type Props = {
  keywords: KeywordStat[];
  themes?: KeywordTheme[];
  segments: SpeechSegment[];
  speakerTimelines: SpeakerTimeline[];
  onSeek: (seconds: number) => void;
  onOpenThemeReplicas?: (theme: KeywordTheme) => void;
  onOpenKeywordReplicas?: (word: string) => void;
};

const KeywordsPanel: React.FC<Props> = ({
  keywords,
  themes,
  segments,
  speakerTimelines,
  onSeek,
  onOpenThemeReplicas,
  onOpenKeywordReplicas,
}) => {
  const resolvedThemes = useMemo(
    () => normalizeThemes(themes, keywords),
    [themes, keywords],
  );

  const [activeThemeIndex, setActiveThemeIndex] = useState(0);
  const [selectedKeyword, setSelectedKeyword] = useState<string | null>(null);

  const safeIndex = Math.min(activeThemeIndex, Math.max(resolvedThemes.length - 1, 0));
  const activeTheme = resolvedThemes[safeIndex];

  useEffect(() => {
    const first = activeTheme?.keywords[0]?.word ?? null;
    setSelectedKeyword(first);
  }, [safeIndex, activeTheme?.label]);

  const totalMentions = useMemo(
    () => keywords.reduce((sum, kw) => sum + kw.count, 0),
    [keywords],
  );

  const keywordReplicaCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const kw of keywords) {
      counts.set(kw.word, countKeywordReplicas(segments, kw.word));
    }
    for (const theme of resolvedThemes) {
      for (const kw of theme.keywords) {
        if (!counts.has(kw.word)) {
          counts.set(kw.word, countKeywordReplicas(segments, kw.word));
        }
      }
    }
    return counts;
  }, [keywords, resolvedThemes, segments]);

  const getReplicaCount = (word: string, fallback = 0) => (
    segments.length ? (keywordReplicaCounts.get(word) ?? fallback) : fallback
  );

  const keywordReplicas = useMemo(
    () => (
      selectedKeyword
        ? buildKeywordReplicas(segments, selectedKeyword)
        : []
    ),
    [selectedKeyword, segments],
  );

  const selectedKeywordCount = selectedKeyword
    ? getReplicaCount(selectedKeyword, activeTheme?.keywords.find(k => k.word === selectedKeyword)?.count || 0)
    : 0;

  const maxBarCount = useMemo(
    () => Math.max(
      ...(activeTheme?.keywords.map(k => getReplicaCount(k.word, k.count)) || [1]),
      1,
    ),
    [activeTheme, keywordReplicaCounts, segments.length],
  );

  const selectTheme = (index: number, keyword?: string) => {
    setActiveThemeIndex(index);
    if (keyword) setSelectedKeyword(keyword);
  };

  if (!keywords.length || !resolvedThemes.length) return null;

  const highlightWord = selectedKeyword ? [selectedKeyword] : [];

  return (
    <div className={cn('keywords-panel')}>
      <p className={cn('keywords-stats-header')}>
        КЛЮЧЕВЫЕ СЛОВА · {keywords.length} ТЕРМИНОВ · {totalMentions} УПОМИНАНИЙ
      </p>

      <div className={cn('keywords-theme-grid')}>
        {resolvedThemes.map((theme, index) => {
          const isActive = index === safeIndex;
          return (
            <button
              key={theme.label}
              type="button"
              className={cn('keywords-theme-card', { active: isActive })}
              style={{
                borderColor: isActive ? theme.color : '#e5e7eb',
                boxShadow: isActive ? `0 0 0 1px ${theme.color}` : undefined,
              }}
              onClick={() => selectTheme(index)}
            >
              <span
                className={cn('keywords-theme-card-icon')}
                style={{
                  backgroundColor: `${theme.color}18`,
                  color: theme.color,
                }}
              >
                <ThemeIcon name={theme.icon} color={theme.color} size={13} />
              </span>
              <span className={cn('keywords-theme-card-title')}>{theme.label}</span>
              <span className={cn('keywords-theme-card-count')}>
                {theme.mention_count} упоминаний
              </span>
              <div className={cn('keywords-theme-card-tags')}>
                {theme.keywords.slice(0, 4).map(kw => {
                  const tagActive = isActive && selectedKeyword === kw.word;
                  return (
                    <span
                      key={kw.word}
                      role="button"
                      tabIndex={0}
                      className={cn('keywords-theme-card-tag', { active: tagActive })}
                      style={{
                        backgroundColor: tagActive ? `${theme.color}28` : '#f3f4f6',
                        color: tagActive ? theme.color : '#4b5563',
                        border: tagActive ? `1px solid ${theme.color}` : '1px solid #e5e7eb',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        selectTheme(index, kw.word);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          selectTheme(index, kw.word);
                        }
                      }}
                    >
                      {formatKeywordLabel(kw.word)} ×{getReplicaCount(kw.word, kw.count)}
                    </span>
                  );
                })}
              </div>
            </button>
          );
        })}
      </div>

      {activeTheme && selectedKeyword && (
        <div className={cn('keywords-detail')}>
          <div className={cn('keywords-detail-head')}>
            <div>
              <h4 className={cn('keywords-detail-title')}>
                Тема: {activeTheme.label.toLowerCase()}
              </h4>
              {activeTheme.insight && (
                <p className={cn('keywords-detail-insight')}>{activeTheme.insight}</p>
              )}
            </div>
            <button
              type="button"
              className={cn('keywords-detail-link')}
              onClick={() => (
                onOpenKeywordReplicas
                  ? onOpenKeywordReplicas(selectedKeyword)
                  : onOpenThemeReplicas?.(activeTheme)
              )}
            >
              В транскрипции ↗
            </button>
          </div>

          <div className={cn('keywords-detail-bars')}>
            {activeTheme.keywords.map(kw => {
              const isSelected = selectedKeyword === kw.word;
              return (
                <button
                  key={kw.word}
                  type="button"
                  className={cn('keywords-detail-bar-row', { active: isSelected })}
                  style={isSelected ? { borderColor: activeTheme.color } : undefined}
                  onClick={() => setSelectedKeyword(kw.word)}
                >
                  <span
                    className={cn('keywords-detail-bar-label')}
                    style={isSelected ? { color: activeTheme.color } : undefined}
                  >
                    {formatKeywordLabel(kw.word)}
                  </span>
                  <div className={cn('keywords-detail-bar-track')}>
                    <div
                      className={cn('keywords-detail-bar-fill')}
                      style={{
                        width: `${(getReplicaCount(kw.word, kw.count) / maxBarCount) * 100}%`,
                        backgroundColor: activeTheme.color,
                        opacity: isSelected ? 1 : 0.55,
                      }}
                    />
                  </div>
                  <span className={cn('keywords-detail-bar-count')}>
                    {getReplicaCount(kw.word, kw.count)}
                  </span>
                </button>
              );
            })}
          </div>

          <div className={cn('keywords-examples')}>
            <p className={cn('keywords-examples-title')}>
              {formatKeywordLabel(selectedKeyword).toUpperCase()} · {selectedKeywordCount} реплик
            </p>
            {keywordReplicas.length === 0 ? (
              <p className={cn('keywords-examples-empty')}>Совпадений в транскрипте не найдено.</p>
            ) : (
              keywordReplicas.map((item, i) => {
                const color = resolveSpeakerColor(item.speaker, speakerTimelines);
                const seekSec = getSegmentStartSec(item.segment);
                return (
                  <button
                    key={`${item.segment.id ?? i}-${item.timeLabel}-${i}`}
                    type="button"
                    className={cn('keywords-example-row')}
                    onClick={() => onSeek(seekSec)}
                    title="Перемотать к этой реплике"
                  >
                    <div className={cn('keywords-example-meta')}>
                      <span
                        className={cn('keywords-example-time')}
                        style={{ color: activeTheme.color }}
                      >
                        {item.timeLabel}
                      </span>
                      <span className={cn('keywords-example-speaker')}>
                        <span
                          className={cn('keywords-example-dot')}
                          style={{ backgroundColor: color }}
                        />
                        {formatSpeakerFullName(item.speaker)}
                      </span>
                      <span
                        className={cn('keywords-example-badge', { dispute: item.badge.isDispute })}
                        style={
                          item.badge.isDispute
                            ? undefined
                            : {
                                backgroundColor: `${activeTheme.color}14`,
                                color: activeTheme.color,
                              }
                        }
                      >
                        {item.badge.label}
                      </span>
                    </div>
                    <p
                      className={cn('keywords-example-text')}
                      dangerouslySetInnerHTML={{
                        __html: highlightWords(
                          item.segment.text?.trim() || '',
                          highlightWord,
                          activeTheme.color,
                        ),
                      }}
                    />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordsPanel;
